import { format as dateFnsFormat, formatDistanceToNow, isValid, parse, parseISO } from 'date-fns';

import { DEFAULT_DATE_FORMAT } from '#constants/date';
import { isDefined } from '#misc/is-defined.guard';

import type { DateFormatOptions } from './types/date-format.options';

/**
 * Formats a given date according to the specified format or options.
 *
 * String parsing is resilient by design (Datastream APIs are not consistent — DateTime vs
 * DateTime2, and Microsoft's JSON serializer trims trailing fractional zeros, so the same column
 * can arrive as `…59`, `…59.12`, or `…59.1234567`, with or without a `Z`/offset):
 *
 * 1. The value is parsed as ISO-8601 first via `parseISO`, which tolerates variable fractional-second
 *    precision and an optional `Z`/offset, with correct UTC-vs-local semantics.
 * 2. If that yields an invalid date and an explicit `parseFormat` is supplied, it falls back to
 *    `parse(value, parseFormat)` for genuinely non-ISO strings (e.g. `MM/dd/yyyy`).
 * 3. If the value still cannot be parsed, the original string is returned unchanged — this function
 *    never throws on an unparseable value.
 *
 * - If `isRelative` is true, returns a human-readable relative time (e.g., "3 days ago").
 * - Otherwise, formats the (parsed) date using the provided or default format string.
 *
 * @param date - The date to format, either as a `Date` object or a string.
 * @param format - The format to use, either as a format string or a `DateFormatOptions` object.
 * @returns The formatted date string, or the original string when it cannot be parsed.
 */
export const formatDate = (date: Date | string, format: DateFormatOptions | string = DEFAULT_DATE_FORMAT): string => {
  const isOptions = typeof format !== 'string';
  const formatString = isOptions ? (format.format ?? DEFAULT_DATE_FORMAT) : format;
  const isRelative = isOptions ? (format.isRelative ?? false) : false;
  const parseFormat = isOptions ? format.parseFormat : undefined;

  let parsed: Date;

  if (typeof date === 'string') {
    // 1. ISO-8601 first — handles variable fractional precision and an optional Z/offset.
    parsed = parseISO(date);

    // 2. Non-ISO fallback when an explicit parseFormat is supplied.
    if (!isValid(parsed) && isDefined(parseFormat)) {
      parsed = parse(date, parseFormat, new Date());
    }

    // 3. Unparseable — return the raw value rather than throwing on an Invalid Date.
    if (!isValid(parsed)) {
      return date;
    }
  } else {
    parsed = date;
  }

  return isRelative
    ? formatDistanceToNow(parsed, { addSuffix: true, includeSeconds: true })
    : dateFnsFormat(parsed, formatString);
};
